import React, { useState } from "react";
import {
	Box,
	Typography,
	Button,
	Grid,
	Paper,
	Skeleton,
	Dialog,
	DialogTitle,
	DialogContent,
	LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EastIcon from "@mui/icons-material/East";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import StampsIcon from "../../assets/sellos.png";
import StampsIconMobile from "../../assets/sellos-2.png";
import { useWindowSize } from "@uidotdev/usehooks";

const TRAVELER_LEVELS = [
	{ label: "Viajero", minCountries: 0, maxCountries: 4 },
	{ label: "Explorador", minCountries: 5, maxCountries: 14 },
	{ label: "Gran explorador", minCountries: 15, maxCountries: 29 },
	{ label: "Viajero experto", minCountries: 30, maxCountries: 49 },
	{ label: "Globetrotter", minCountries: 50, maxCountries: 79 },
	{ label: "Ciudadano del mundo", minCountries: 80, maxCountries: null },
];

const getCurrentLevelProgress = (countriesCount) => {
	const level = TRAVELER_LEVELS.find((l) =>
		l.maxCountries === null
			? countriesCount >= l.minCountries
			: countriesCount >= l.minCountries && countriesCount <= l.maxCountries,
	);
	if (!level) return 0;
	if (level.maxCountries === null) return 100;
	const range = level.maxCountries - level.minCountries + 1;
	const progress = countriesCount - level.minCountries;
	return Math.min(100, Math.round((progress / range) * 100));
};

const WelcomeSection = ({
	user,
	onCreateFromScratch,
	onExploreGuides,
	onCreateRoute,
	onGoToPassport,
	onGoToTravelCard,
	onGoToMyGuides,
	guidesCount = 0,
	loading = false,
	countriesCount = 0,
	hasTravelCard = false,
	travelerLevelLabel = "Viajero",
}) => {
	const size = useWindowSize();
	const [levelsModalOpen, setLevelsModalOpen] = useState(false);

	const greeting = () => {
		const hour = new Date().getHours();
		if (hour < 12) return "Buenos días";
		if (hour < 18) return "Buenas tardes";
		return "Buenas noches";
	};

	const quickActions = [
		{
			id: "create",
			title: "Crear desde cero",
			description: "Empieza con un lienzo en blanco",
			icon: <AddIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />,
			gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
			onClick: onCreateFromScratch,
		},
		{
			id: "template",
			title: "Usar nuestras guías",
			description: "Rutas listas para viajar",
			icon: <AutoAwesomeIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />,
			gradient: "linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
			onClick: onExploreGuides,
		},
		{
			id: "passport",
			title: "Tu pasaporte viajero",
			description: "Sellos y progreso viajero",
			icon: <MenuBookOutlinedIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />,
			gradient: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
			onClick: () => onGoToPassport?.(),
		},
		{
			id: "travel-card",
			title: "Tarjeta viajera",
			description: "Tu identidad viajera",
			icon: <BadgeOutlinedIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />,
			gradient: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)",
			onClick: () => onGoToTravelCard?.(),
		},
	];

	const showQuickActions = !!user;

	const stats = [
		{
			label: "Guías",
			value: guidesCount.toString(),
			icon: <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />,
			onClick: () => onGoToMyGuides?.(),
		},
		{
			label: "Destinos",
			value: countriesCount.toString(),
			icon: <FlightTakeoffIcon sx={{ fontSize: 18 }} />,
			onClick: () => onGoToPassport?.(),
		},
		{
			label: "Tarjeta",
			value: hasTravelCard ? "Activa" : "Crear",
			icon: <BadgeOutlinedIcon sx={{ fontSize: 18 }} />,
			onClick: () => onGoToTravelCard?.(),
		},
		{
			label: "Nivel",
			value: travelerLevelLabel,
			icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 18 }} />,
			onClick: () => setLevelsModalOpen(true),
			showProgress: true,
		},
	];

	const levelProgress = getCurrentLevelProgress(countriesCount);

	return (
		<Box
			sx={{
				marginTop: size.width >= 600 && size.width < 920 ? "-23px" : 0,
				marginLeft: size.width >= 600 && size.width < 920 ? "-24px" : 0,
				width:
					size.width >= 600 && size.width < 920 ? "calc(100% + 48px)" : "100%",
				mb: { xs: 2.5, md: 5 },
			}}
		>
			{/* Hero Banner */}
			<Box
				sx={{
					position: "relative",
					borderRadius: size.width <= 920 ? "0 0 24px 24px" : "24px",
					overflow: "hidden",
					mb: { xs: showQuickActions ? 2 : 0, md: 4 },
					boxShadow: "none",
				}}
			>
				<Box
					sx={{
						background:
							"linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
						px: { xs: 2, sm: 3.5, md: 5 },
						pb: { xs: 2.5, sm: 3.5, md: 5 },
						pt: { xs: "84px", sm: "96px", md: 6 },
						position: "relative",
						overflow: "hidden",
						boxShadow: "none",
					}}
				>
					{/* Decorative blurs */}
					<Box
						sx={{
							position: "absolute",
							top: -100,
							right: -100,
							width: 400,
							height: 400,
							borderRadius: "50%",
							background:
								"radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
							filter: "blur(60px)",
						}}
					/>
					<Box
						sx={{
							position: "absolute",
							bottom: -150,
							left: -50,
							width: 300,
							height: 300,
							borderRadius: "50%",
							background:
								"radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)",
							filter: "blur(40px)",
						}}
					/>

					{/* Desktop illustration */}
					{size.width > 1280 && (
						<Box
							sx={{
								position: "absolute",
								top: "50%",
								right: { lg: "6%", xl: "8%" },
								transform: "translateY(-50%)",
								pointerEvents: "none",
							}}
						>
							<Box
								sx={{
									position: "relative",
									width:
										size.width > 1600 ? 460 : size.width > 1280 ? 360 : 260,
									height:
										size.width > 1600 ? 320 : size.width > 1280 ? 240 : 180,
								}}
							>
								<Box
									sx={{
										position: "absolute",
										inset: 0,
										borderRadius: "22px",
										background: "rgba(255,255,255,0.06)",
										backdropFilter: "blur(26px)",
										border: "1px solid rgba(255,255,255,0.10)",
										transform: "rotate(-8deg) translate(-14px, 10px)",
									}}
								/>
								<Box
									sx={{
										position: "absolute",
										inset: 0,
										borderRadius: "22px",
										background: "rgba(255,255,255,0.08)",
										backdropFilter: "blur(26px)",
										border: "1px solid rgba(255,255,255,0.12)",
										transform: "rotate(4deg) translate(10px, 14px)",
									}}
								/>
								<Box
									sx={{
										position: "absolute",
										inset: 0,
										borderRadius: "22px",
										background: "rgba(255,255,255,0.10)",
										backdropFilter: "blur(28px)",
										border: "1px solid rgba(255,255,255,0.14)",
										transform: "rotate(-2deg)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										overflow: "hidden",
									}}
								>
									<Box
										component="img"
										src={StampsIcon}
										alt=""
										sx={{
											width:
												size.width > 1600 ? 380 : size.width > 1280 ? 280 : 180,
											height:
												size.width > 1600 ? 380 : size.width > 1280 ? 280 : 180,
											objectFit: "contain",
											transform: "rotate(6deg) translateY(6px)",
										}}
									/>
								</Box>
							</Box>
						</Box>
					)}

					{/* Content */}
					<Box
						sx={{
							position: "relative",
							zIndex: 1,
							maxWidth:
								size.width > 1600
									? 640
									: size.width > 1280
										? "calc(100% - 475px)"
										: "100%",
						}}
					>
						<Typography
							sx={{
								color: "rgba(255, 255, 255, 0.6)",
								fontSize: { xs: "0.688rem", sm: "0.875rem" },
								fontWeight: 600,
								letterSpacing: "0.05em",
								textTransform: "uppercase",
								mb: { xs: 0.5, sm: 1.5 },
								display: { xs: "none", sm: "block" },
							}}
						>
							{greeting()}
							{user ? `, ${user.displayName || "viajero"}` : ""}
						</Typography>

						<Typography
							variant="h3"
							sx={{
								color: "#fff",
								fontWeight: 700,
								fontSize: { xs: "1.375rem", sm: "2.25rem", md: "2.75rem" },
								lineHeight: 1.15,
								mb: { xs: 1, sm: 2 },
								letterSpacing: "-0.02em",
							}}
						>
							Tus{" "}
							<Box
								component="span"
								sx={{
									background:
										"linear-gradient(135deg, #A855F7 0%, #6366F1 50%, #22D3EE 100%)",
									backgroundClip: "text",
									WebkitBackgroundClip: "text",
									WebkitTextFillColor: "transparent",
								}}
							>
								guías de viaje
							</Box>{" "}
							para tenerlo todo organizado
						</Typography>

						{/* Subtitle - tablet/desktop */}
						<Typography
							sx={{
								color: "rgba(255, 255, 255, 0.65)",
								fontSize: { xs: "0.875rem", md: "1.125rem" },
								lineHeight: 1.5,
								mb: { xs: 1.5, lg: 4 },
								maxWidth: 560,
								display: { xs: "block", sm: "block" },
							}}
						>
							Todo lo que necesitas para preparar tu viaje: itinerarios,
							mapas, rutas y herramientas prácticas.
						</Typography>

						{/* Stamps illustration - mobile */}
						{size.width <= 1280 && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									mb: "48px",
									mt: size.width < 1280 ? "24px" : 0,
								}}
							>
								<Box
									component="img"
									src={StampsIconMobile}
									alt=""
									sx={{
										width: "100%",
										maxWidth: size.width > 1000 ? 400 : 280,
										height: "auto",
										objectFit: "contain",
										opacity: 0.9,
									}}
								/>
							</Box>
						)}

						{/* CTA Buttons — data-nosnippet evita que Google use el texto del botón en el snippet */}
						<Box
							data-nosnippet
							sx={{
								display: "flex",
								gap: { xs: 1, sm: 2 },
								flexWrap: "wrap",
								justifyContent: size.width < 1280 ? "center" : "flex-start",
							}}
						>
							<Button
								variant="contained"
								startIcon={
									<ExploreOutlinedIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
								}
								endIcon={
									<EastIcon
										sx={{
											fontSize: 18,
											display: { xs: "none", sm: "block" },
										}}
									/>
								}
								onClick={onExploreGuides}
								sx={{
									background: "#A78BFA",
									color: "#ffffff !important",
									textTransform: "none",
									fontWeight: 600,
									fontSize: { xs: "0.813rem", sm: "0.938rem" },
									px: { xs: 2, sm: 3 },
									py: { xs: 1, sm: 1.5 },
									borderRadius: { xs: "12px", sm: "14px" },
									boxShadow: "none",
									whiteSpace: "nowrap",
									flex: { xs: 1, sm: "none" },
									"& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root":
										{ color: "#ffffff !important" },
									"&:hover": {
										background: "#8B5CF6",
										boxShadow: "none",
										transform: "translateY(-2px)",
										color: "#ffffff !important",
										"& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root":
											{ color: "#ffffff !important" },
									},
									transition: "all 0.2s ease",
								}}
							>
								Guías
							</Button>
							<Button
								variant="outlined"
								startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />}
								onClick={onCreateFromScratch}
								sx={{
									borderWidth: "1.5px",
									borderColor: "rgba(255, 255, 255, 0.9)",
									color: "#ffffff !important",
									backgroundColor: "rgba(255, 255, 255, 0.06)",
									textTransform: "none",
									fontWeight: 600,
									fontSize: { xs: "0.813rem", sm: "0.938rem" },
									px: { xs: 2, sm: 3 },
									py: { xs: 1, sm: 1.5 },
									borderRadius: { xs: "12px", sm: "14px" },
									whiteSpace: "nowrap",
									flex: { xs: 1, sm: "none" },
									"& .MuiButton-startIcon .MuiSvgIcon-root": {
										color: "#ffffff !important",
									},
									"&:hover": {
										borderColor: "rgba(255, 255, 255, 0.95)",
										backgroundColor: "rgba(255, 255, 255, 0.12)",
										color: "#ffffff !important",
										"& .MuiButton-startIcon .MuiSvgIcon-root": {
											color: "#ffffff !important",
										},
									},
								}}
							>
								Crear guía
							</Button>
							{onCreateRoute && (
								<Button
									variant="outlined"
									startIcon={
										<MapOutlinedIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
									}
									onClick={onCreateRoute}
									sx={{
										borderWidth: "1.5px",
										borderColor: "rgba(167, 139, 250, 0.85)",
										color: "#ffffff !important",
										backgroundColor: "rgba(99, 102, 241, 0.28)",
										textTransform: "none",
										fontWeight: 600,
										fontSize: { xs: "0.813rem", sm: "0.938rem" },
										px: { xs: 2, sm: 3 },
										py: { xs: 1, sm: 1.5 },
										borderRadius: { xs: "12px", sm: "14px" },
										whiteSpace: "nowrap",
										flex: { xs: "1 1 100%", sm: "none" },
										"& .MuiButton-startIcon .MuiSvgIcon-root": {
											color: "#ffffff !important",
										},
										"&:hover": {
											borderColor: "rgba(196, 181, 253, 0.95)",
											backgroundColor: "rgba(99, 102, 241, 0.42)",
											color: "#ffffff !important",
											"& .MuiButton-startIcon .MuiSvgIcon-root": {
												color: "#ffffff !important",
											},
										},
									}}
								>
									Crea tu ruta
								</Button>
							)}
						</Box>

						{/* === Logged: Mini-dashboard stats === */}
						{user ? (
							<Box
								sx={{
									mt: { xs: 2, sm: 3 },
									pt: { xs: 1.5, sm: 3 },
									borderTop: "1px solid rgba(255, 255, 255, 0.1)",
								}}
							>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "repeat(4, 1fr)",
										gap: { xs: 0.5, sm: 3 },
									}}
								>
									{loading
										? [1, 2, 3, 4].map((key) => (
												<Box key={key} sx={{ textAlign: "center" }}>
													<Skeleton
														variant="rounded"
														width={32}
														height={32}
														sx={{
															bgcolor: "rgba(255, 255, 255, 0.12)",
															borderRadius: "8px",
															mx: "auto",
															mb: 0.5,
														}}
													/>
													<Skeleton
														variant="text"
														width={36}
														height={18}
														sx={{
															bgcolor: "rgba(255, 255, 255, 0.12)",
															mx: "auto",
														}}
													/>
												</Box>
											))
										: stats.map((stat, index) => (
												<Box
													key={index}
													onClick={stat.onClick}
													sx={{
														cursor: stat.onClick ? "pointer" : "default",
														textAlign: "center",
														py: { xs: 0.75, sm: 0 },
														borderRadius: "12px",
														transition: "all 0.15s ease",
														"&:hover": stat.onClick
															? {
																	backgroundColor: "rgba(255, 255, 255, 0.05)",
																}
															: {},
													}}
												>
													<Box
														sx={{
															width: { xs: 32, sm: 36 },
															height: { xs: 32, sm: 36 },
															borderRadius: { xs: "8px", sm: "10px" },
															backgroundColor: "rgba(255, 255, 255, 0.1)",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															color: "rgba(255, 255, 255, 0.7)",
															mx: "auto",
															mb: 0.5,
														}}
													>
														{stat.icon}
													</Box>
													<Typography
														sx={{
															color: "#fff",
															fontWeight: 700,
															fontSize: { xs: "0.938rem", sm: "1.25rem" },
															lineHeight: 1,
															mb: 0.25,
														}}
													>
														{stat.value}
													</Typography>
													<Typography
														sx={{
															color: "rgba(255, 255, 255, 0.5)",
															fontSize: { xs: "0.625rem", sm: "0.75rem" },
															lineHeight: 1.2,
														}}
													>
														{stat.label}
													</Typography>
													{stat.showProgress && (
														<LinearProgress
															variant="determinate"
															value={levelProgress}
															sx={{
																mt: 0.5,
																height: 3,
																borderRadius: 2,
																maxWidth: { xs: 40, sm: 64 },
																mx: "auto",
																backgroundColor: "rgba(255,255,255,0.1)",
																"& .MuiLinearProgress-bar": {
																	borderRadius: 2,
																	background:
																		"linear-gradient(90deg, #A855F7, #6366F1)",
																},
															}}
														/>
													)}
												</Box>
											))}
								</Box>
							</Box>
						) : (
							/* === Not logged: Social proof === */
							<Box
								sx={{
									mt: { xs: 1.5, sm: 3 },
									pt: { xs: 1, sm: 2.5 },
									borderTop: "1px solid rgba(255, 255, 255, 0.08)",
									display: "flex",
									alignItems: "center",
									gap: 0.75,
								}}
							>
								<PeopleOutlinedIcon
									sx={{
										fontSize: 16,
										color: "rgba(255,255,255,0.4)",
										flexShrink: 0,
									}}
								/>
								<Typography
									sx={{
										color: "rgba(255, 255, 255, 0.5)",
										fontSize: { xs: "0.688rem", sm: "0.875rem" },
										fontWeight: 500,
									}}
								>
									+1.200 viajeros crean sus guías con nosotros
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</Box>

			{/* Quick Actions — solo para usuarios logueados */}
			{showQuickActions && (
				<Box sx={{ px: { xs: 2, md: 0 } }}>
					{/* Mobile: scroll horizontal */}
					<Box
						sx={{
							display: { xs: "flex", sm: "none" },
							gap: 1,
							overflowX: "auto",
							pb: 0.5,
							"&::-webkit-scrollbar": { display: "none" },
							scrollbarWidth: "none",
						}}
					>
						{quickActions.map((action) => (
							<Paper
								key={action.id}
								onClick={action.onClick}
								elevation={0}
								sx={{
									p: 1.5,
									borderRadius: "14px",
									border: "1px solid rgba(0, 0, 0, 0.06)",
									boxShadow: "none",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									backgroundColor: "#fff",
									flexShrink: 0,
									minWidth: "auto",
									transition: "all 0.2s ease",
									"&:active": {
										transform: "scale(0.97)",
									},
								}}
							>
								<Box
									sx={{
										width: 36,
										height: 36,
										borderRadius: "10px",
										background: action.gradient,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: "#fff",
										flexShrink: 0,
									}}
								>
									{action.icon}
								</Box>
								<Typography
									sx={{
										fontWeight: 600,
										color: "#1a1a2e",
										fontSize: "0.813rem",
										lineHeight: 1.3,
										whiteSpace: "nowrap",
									}}
								>
									{action.title}
								</Typography>
							</Paper>
						))}
					</Box>

					{/* Desktop/Tablet: grid */}
					<Grid
						container
						spacing={2.5}
						sx={{ display: { xs: "none", sm: "flex" } }}
						className="stagger-animation"
					>
						{quickActions.map((action) => (
							<Grid item sm={6} lg={3} key={action.id}>
								<Paper
									onClick={action.onClick}
									elevation={0}
									sx={{
										p: 2.5,
										borderRadius: "18px",
										border: "1px solid rgba(0, 0, 0, 0.06)",
										boxShadow: "none",
										cursor: "pointer",
										transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
										display: "flex",
										alignItems: "center",
										gap: 2,
										backgroundColor: "#fff",
										"&:hover": {
											transform: "translateY(-4px)",
											boxShadow: "none",
											borderColor: "transparent",
											"& .action-icon": {
												transform: "scale(1.05)",
											},
											"& .action-arrow": {
												opacity: 1,
												transform: "translateX(0)",
											},
										},
									}}
								>
									<Box
										className="action-icon"
										sx={{
											width: 56,
											height: 56,
											borderRadius: "14px",
											background: action.gradient,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "#fff",
											flexShrink: 0,
											transition: "transform 0.25s ease",
										}}
									>
										{action.icon}
									</Box>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											sx={{
												fontWeight: 600,
												color: "#1a1a2e",
												fontSize: "0.938rem",
												mb: 0.25,
												lineHeight: 1.3,
											}}
										>
											{action.title}
										</Typography>
										<Typography
											sx={{
												color: "#8B8FA3",
												fontSize: "0.813rem",
												lineHeight: 1.4,
											}}
										>
											{action.description}
										</Typography>
									</Box>
									<EastIcon
										className="action-arrow"
										sx={{
											color: "#8B8FA3",
											fontSize: 20,
											opacity: 0,
											transform: "translateX(-8px)",
											transition: "all 0.25s ease",
										}}
									/>
								</Paper>
							</Grid>
						))}
					</Grid>
				</Box>
			)}

			{/* Modal: niveles viajeros */}
			<Dialog
				open={levelsModalOpen}
				onClose={() => setLevelsModalOpen(false)}
				PaperProps={{
					sx: {
						borderRadius: "16px",
						maxWidth: 400,
						width: "100%",
						boxShadow: "none",
					},
				}}
			>
				<DialogTitle
					sx={{
						fontWeight: 700,
						fontSize: "1.125rem",
						color: "#1a1a2e",
						pb: 0,
						pt: 2,
						px: 2.5,
						display: "flex",
						alignItems: "center",
						gap: 1.25,
					}}
				>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: "10px",
							background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "#fff",
						}}
					>
						<WorkspacePremiumOutlinedIcon sx={{ fontSize: 22 }} />
					</Box>
					Niveles viajeros
				</DialogTitle>
				<DialogContent sx={{ pt: 1.5, pb: 2.5, px: 2.5 }}>
					<Typography
						sx={{
							color: "#6B7280",
							fontSize: "0.875rem",
							lineHeight: 1.5,
							mb: 2,
							mt: 2,
						}}
					>
						Desbloquea niveles visitando países y añadiendo sellos a tu
						pasaporte.
					</Typography>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 0.5,
							borderRadius: "12px",
							overflow: "hidden",
							border: "1px solid rgba(0,0,0,0.06)",
						}}
					>
						{TRAVELER_LEVELS.map((level, index) => {
							const isCurrent =
								level.maxCountries === null
									? countriesCount >= level.minCountries
									: countriesCount >= level.minCountries &&
										countriesCount <= level.maxCountries;
							const isUnlocked = countriesCount >= level.minCountries;
							return (
								<Box
									key={index}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										py: 1.25,
										px: 1.5,
										backgroundColor: isCurrent
											? "rgba(99, 102, 241, 0.06)"
											: index % 2 === 1
												? "rgba(0,0,0,0.02)"
												: "transparent",
										borderLeft: "3px solid transparent",
										...(isCurrent && {
											borderLeftColor: "#6366F1",
										}),
									}}
								>
									<Typography
										sx={{
											fontWeight: isCurrent ? 600 : 500,
											fontSize: "0.938rem",
											color: isUnlocked ? "#1a1a2e" : "#9CA3AF",
										}}
									>
										{level.label}
									</Typography>
									<Typography
										sx={{
											fontSize: "0.813rem",
											color: isCurrent ? "#6366F1" : "#6B7280",
											fontWeight: isCurrent ? 600 : 400,
										}}
									>
										{level.maxCountries !== null
											? `${level.minCountries}–${level.maxCountries}`
											: `${level.minCountries}+`}
									</Typography>
								</Box>
							);
						})}
					</Box>
					<Typography sx={{ mt: 1.5, fontSize: "0.75rem", color: "#9CA3AF" }}>
						Países con sellos en tu pasaporte
					</Typography>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default WelcomeSection;
